import { Edit, Visibility } from '@mui/icons-material';
import { Grid, IconButton, Switch } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGrid, GridColDef, GridSortModel, gridClasses } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getAllInformationCategory,
  createAllInformationCategory,
  updateAllInformationCategory,
  InformationCategoryDTO,
} from 'wisestep-api';
import {
  getDateUsingFormat,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import ErrorLogger from '../../../src/providers/ErrorLogger';
import { SettingsPageLayout } from '../../components/SettingsPageLayout';
import { SurveyInfo } from '../../components/SurveyBuilder/SurveyInfo';
import { useWorkspaceContext } from '../../context';
import { success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { informationReqAmplitudeEvents } from '../conversationWidget/utils/rtrHelper';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';

import {
  IInformationCategoryStatuses,
  defaultDiceSearchStateReq,
} from './helper/SurvayStatusHelper';

export const informationCategoryDTOHelper: InformationCategoryDTO = {
  id: '',
  workspaceId: '',
  createdBy: '',
  createdOn: '',
  categoryName: '',
  updatedBy: '',
  updated_on: '',
  countOfInformation: '',
  active: false,
  category: '',
  rtrTemplate: '',
  rtrOption1: '',
  rtrStatus1: '',
  rtrOption2: '',
  rtrStatus2: '',
};
const useStyles = makeStyles({
  customButton: {
    textTransform: 'none',
    padding: '12px 24px',
    maxWidth: '300px',
    whiteSpace: 'nowrap',
    lineHeight: '1.4',
  },
});
const InformationCategories = () => {
  const classes = useStyles();
  const workspace = useWorkspaceContext();
  const methods = useForm();
  const [informationListData, setInformationListData] = useState<InformationCategoryDTO[]>([]);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'categoryName', sort: 'asc' },
  ]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [state, setState] = useState<IInformationCategoryStatuses>(defaultDiceSearchStateReq);

  const getInformationCategoryList = useCallback(async () => {
    try {
      const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
      const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
      const response = await getAllInformationCategory(pageNo, pageSize, '', sortBy, sortOrder);
      setTotalRecord(response?.data.totalElements);
      setInformationListData(response?.data.content);
    } catch (error) {
      console.error('Error fetching information categories:', error);
    }
  }, [sortModel, pageNo, pageSize]);

  useEffect(() => {
    setState((m) => ({
      ...m,
      showInfoSurvey: false,
    }));
    getInformationCategoryList();
  }, [getInformationCategoryList]);

  const createInfoCategory = () => {
    setState((m) => ({
      ...m,
      showInfoSurvey: true,
      modeType: 0,
      categoryId: '',
      categoryJsonObject: null,
      getSelectedRTRTemplateName: '',
      getSelectedAssociateTemplateName: '',
    }));
  };

  const callBackUpdateSurvay = async (data: any) => {
    const avPayload = { ...informationCategoryDTOHelper };
    avPayload.id = data.id;
    avPayload.countOfInformation = data.countOfInformation;
    avPayload.category = data.category;
    avPayload.categoryName = data.categoryName;
    avPayload.active = data.active;
    avPayload.rtrTemplate = data.rtrTemplate;
    avPayload.rtrOption1 = data.rtrOption1;
    avPayload.rtrStatus1 = data.rtrStatus1;
    avPayload.rtrOption2 = data.rtrOption2;
    avPayload.rtrStatus2 = data.rtrStatus2;

    if (data.categoryName === undefined || data.categoryName === '') {
      setNotification(warning(`Oops , Category Name is missing !!`));
    } else {
      try {
        await updateAllInformationCategory(data.id, avPayload);
        setNotification(success(`${data.categoryName} has been updated successfully !!`));
        getInformationCategoryList();
        showBackCategoryList();
        informationReqAmplitudeEvents(
          'info_template_edit',
          'Information Request template edited',
          workspace
        );
      } catch (error: any) {
        setNotification(warning(`Oops , ${data.categoryName} already exists!!`));
        console.log('error', error);
      }
    }
  };

  const callBackAddSurvay = async (data: any) => {
    if (data.categoryName === undefined || data.categoryName === '') {
      setNotification(warning(`Oops , Category Name is missing !!`));
    } else {
      try {
        await createAllInformationCategory(data);
        setNotification(success(`${data.categoryName} has been added successfully !!`));
        getInformationCategoryList();
        showBackCategoryList();
        informationReqAmplitudeEvents(
          'info_template_created',
          'Information Request template created',
          workspace
        );
      } catch (error: any) {
        setNotification(warning(`Oops , ${data.categoryName} already exists!!`));
        console.log('error', error);
      }
    }
  };
  const showBackCategoryList = () => {
    setState((m) => ({
      ...m,
      showInfoSurvey: false,
      modeType: 0,
      categoryId: '',
      getSelectedRTRTemplateName: '',
      getSelectedRTRResponseField: '',
      getSelectedRTRStatus: '',
      getSelectedRTRResponseField2: '',
      getSelectedRTRStatus2: '',
      getSelectedAssociateTemplateName: '',
    }));
    getInformationCategoryList();
  };

  const handleEditItem = async (id: string) => {
    setState((m) => ({
      ...m,
      showInfoSurvey: true,
      modeType: 1,
      categoryId: informationListData[id].id,
      categoryJsonObject: JSON.parse(JSON.stringify(informationListData[id].category)),
      toggleState: informationListData[id].active,
      getSelectedRTRTemplateName: informationListData[id].rtrTemplate,
      getCountOfPage: informationListData[id].countOfInformation,
      getSelectedRTRResponseField: informationListData[id].rtrOption1,
      getSelectedRTRStatus: informationListData[id].rtrStatus1,
      getSelectedRTRResponseField2: informationListData[id].rtrOption2,
      getSelectedRTRStatus2: informationListData[id].rtrStatus2,
      getSelectedAssociateTemplateName: informationListData[id].categoryName,
    }));
  };
  const viewTamplate = (id: string) => {
    setState((m) => ({
      ...m,
      showInfoSurvey: true,
      modeType: 2,
      categoryId: informationListData[id].id,
      categoryJsonObject: JSON.parse(JSON.stringify(informationListData[id].category)),
      getSelectedRTRTemplateName: informationListData[id].rtrTemplate,
      getSelectedRTRResponseField: informationListData[id].rtrOption1,
      getSelectedRTRStatus: informationListData[id].rtrStatus1,
      getSelectedRTRResponseField2: informationListData[id].rtrOption2,
      getSelectedRTRStatus2: informationListData[id].rtrStatus2,
      getSelectedAssociateTemplateName: informationListData[id].categoryName,
    }));
  };

  const tableData: Record<string, unknown>[] = [];

  informationListData.forEach((informationListData, index) => {
    tableData.push({
      id: informationListData.id,
      categoryName: informationListData.categoryName,
      category: informationListData.category,
      countOfInformation: informationListData.countOfInformation,
      active: informationListData.active,
      createdBy: informationListData.createdBy,
      createdOn: informationListData.createdOn,
      index: index,
      rtrTemplate: informationListData.rtrTemplate,
      rtrOption1: informationListData.rtrOption1,
      rtrStatus1: informationListData.rtrStatus1,
      rtrOption2: informationListData.rtrOption2,
      rtrStatus2: informationListData.rtrStatus2,
    });
  });

  const columns: GridColDef[] = [
    {
      field: 'categoryName',
      headerName: 'Category Name',
      flex: 0.5,
      renderCell: (params) => (
        <WsTypography
          variant={WsTypoVariants.SUB_TITLE}
          bold
          color={WsTypoColors.PRIMARY}
          cursor="pointer"
        >
          {params.row['categoryName']}
        </WsTypography>
      ),
    },
    {
      field: 'countOfInformation',
      headerName: 'Count of Information',
      flex: 0.5,
    },
    {
      headerName: 'Active',
      field: 'active',
      flex: 0.5,
      renderCell: (params) => (
        <Switch
          defaultChecked={params.row.active}
          color="primary"
          onChange={async (_event, checked) => {
            await updateAllInformationCategory(params.row.id, { ...params.row, active: checked });
            getInformationCategoryList();
            setNotification(success(`Information Category saved successfully!`));
          }}
        />
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 0.5,
    },
    {
      field: 'created_on',
      headerName: 'Created On',
      flex: 0.5,
      renderCell: (params) => <>{getDateUsingFormat(params.row['created_on'], 'DD-MMM-YY')}</>,
    },
    {
      field: 'Action',
      headerName: '',
      flex: 0.5,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => viewTamplate(params.row['index'] as string)}>
            <Visibility color="primary" />
          </IconButton>
          <IconButton onClick={() => handleEditItem(params.row['index'] as string)}>
            <Edit color="primary" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <SettingsPageLayout
      title={'Information Categories'}
      indexOptions={[{ label: 'Information Categories', url: '/information-categories' }]}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.CRM,
        Pages['INFORMATION_CATAGORIES']
      )}
    >
      {state.showInfoSurvey ? (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <SurveyInfo
            modeType={state.modeType}
            jsonObject={state.categoryJsonObject}
            categoryId={state.categoryId}
            getRTRTemplateName={state.getSelectedRTRTemplateName}
            getRTRResponseField2={state.getSelectedRTRResponseField2}
            getRTRResponseField={state.getSelectedRTRResponseField}
            getRTRStatus={state.getSelectedRTRStatus}
            getRTRStatus2={state.getSelectedRTRStatus2}
            getCategoryTemplateName={state.getSelectedAssociateTemplateName}
            backTocategory={showBackCategoryList}
            addGetDataBack={callBackAddSurvay}
            updateGetDataBack={callBackUpdateSurvay}
            toggleState={state.toggleState}
            getPageCount={state.getCountOfPage}
          />
        </ErrorBoundary>
      ) : (
        <Grid sx={{ backgroundColor: WsColors.TheBlues50 }} container py={1}>
          <Grid item xs={12} justifyContent="flex-end" pt={2}>
            <FormProvider {...methods}>
              <Grid container sx={{ display: 'flex' }} component="form"></Grid>
            </FormProvider>
          </Grid>
          <Grid item xs={12} sx={{ ml: 8 }} container justifyContent="flex-end">
            <Grid item xs={3} p={1} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <WsButton
                fullWidth
                onClick={() => createInfoCategory()}
                variant="contained"
                size="large"
                className={classes.customButton}
              >
                Create Information Request Template
              </WsButton>
            </Grid>
          </Grid>
          <Grid xs={12} sx={{ backgroundColor: 'white' }}>
            <DataGrid
              autoHeight={true}
              columns={columns}
              rows={tableData}
              onPageChange={(page) => setPageNo(page)}
              page={pageNo}
              rowCount={totalRecord}
              pageSize={pageSize}
              rowsPerPageOptions={[25, 50, 75, 100]}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              onSortModelChange={(newSortModel) => {
                setPageNo(0);
                setSortModel(newSortModel);
              }}
              sortingMode="server"
              paginationMode="server"
              getRowHeight={() => 'auto'}
              sx={{
                [`& .${gridClasses.cell}`]: {
                  py: 1,
                },
              }}
            />
          </Grid>
        </Grid>
      )}
    </SettingsPageLayout>
  );
};

export default InformationCategories;
